import { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../lib/auth/AuthProvider';
import { getMyVault, listRecipients, type RecipientListItem } from '../lib/vault/vaultApi';
import type { Combinator, DeliveryCondition } from '../lib/seal/sealApi';
import { ConditionFields, type ConditionHandle } from './ConditionFields';
import './record.css';

export interface MessageDraft {
  /** The recording (video). Absent for a letter draft. */
  blob?: Blob;
  /** 'video' (default) or 'letter'. A letter carries `letterText` instead of a blob. */
  format?: 'video' | 'letter';
  /** The composed letter text (letter drafts only). */
  letterText?: string;
  recipientId: string;
  recipientName: string;
  /** Kept for compatibility: the calendar date, when the (single) condition is a date. */
  deliveryDate: string;
  /** How this message finds its moment (single-condition). */
  condition?: DeliveryCondition;
  /** Compound "if-then-BUT": 2+ conditions combined by `combinator`. When set
   *  (length > 1) the seal creates a trigger group and attaches each condition. */
  conditions?: DeliveryCondition[];
  combinator?: Combinator;
  /** Recording duration in ms, from the recorder's elapsedMs. */
  durationMs?: number;
}

type LoadState = 'loading' | 'ready' | 'empty' | 'error';

function recipientLabel(r: RecipientListItem): string {
  return r.is_placeholder ? (r.placeholder_label ?? r.full_name) : r.full_name;
}

/** The concrete calendar arrival date, if any — the fallback "if it hasn't
 *  happened by" date is a DEADLINE, not an arrival, so it does NOT count. */
function firstDate(conditions: DeliveryCondition[]): string {
  for (const c of conditions) {
    if (c.kind === 'date') return c.date;
  }
  return '';
}

export function ScheduleView({ blob, letterText, durationMs, onBack, onScheduled, backLabel = 'Retake', heading, allowCompound = true, lockedRecipientId }: {
  blob?: Blob;
  letterText?: string;
  durationMs?: number;
  onBack: () => void;
  onScheduled: (draft: MessageDraft) => void;
  backLabel?: string;
  heading?: { kicker: string; title: string };
  /** Whether to offer the compound (AND/OR) builder. Off for senior review,
   *  whose seal path only configures a single condition. */
  allowCompound?: boolean;
  /** When set, the recipient is LOCKED to this id (already cryptographically
   *  sealed to them) — the picker is forced to it and disabled. */
  lockedRecipientId?: string;
}) {
  const { api } = useAuth();
  const [load, setLoad] = useState<LoadState>('loading');
  const [recipients, setRecipients] = useState<RecipientListItem[]>([]);
  const [recipientId, setRecipientId] = useState('');
  const [error, setError] = useState('');
  const [compound, setCompound] = useState(false);
  const [combinator, setCombinator] = useState<Combinator>('AND');
  const primaryRef = useRef<ConditionHandle>(null);
  const secondRef = useRef<ConditionHandle>(null);

  const previewUrl = useMemo(() => (blob !== undefined ? URL.createObjectURL(blob) : null), [blob]);
  useEffect(() => () => { if (previewUrl !== null) URL.revokeObjectURL(previewUrl); }, [previewUrl]);

  const minDate = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${d.getFullYear()}-${mm}-${dd}`;
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const dash = await getMyVault(api);
        const list = await listRecipients(api, dash.vault.id);
        setRecipients(list.recipients);
        if (list.recipients.length > 0) {
          // A locked recipient (already sealed) wins over the default first pick.
          setRecipientId(lockedRecipientId ?? list.recipients[0].id);
          setLoad('ready');
        } else setLoad('empty');
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Could not load your recipients.');
        setLoad('error');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    const r = recipients.find((x) => x.id === recipientId);
    if (!r) { setError('Choose a recipient.'); return; }

    const c1 = primaryRef.current?.build() ?? null;
    if (c1 === null) return;

    const base = {
      blob,
      format: (letterText !== undefined ? 'letter' : 'video') as 'letter' | 'video',
      letterText,
      recipientId,
      recipientName: recipientLabel(r),
      durationMs,
    };

    if (allowCompound && compound) {
      const c2 = secondRef.current?.build() ?? null;
      if (c2 === null) return;
      const conditions = [c1, c2];
      onScheduled({ ...base, condition: c1, conditions, combinator, deliveryDate: firstDate(conditions) });
      return;
    }

    onScheduled({
      ...base,
      condition: c1,
      deliveryDate: c1.kind === 'date' ? c1.date : firstDate([c1]),
    });
  }

  return (
    <section className="sched">
      <p className="sched__kicker">{heading?.kicker ?? 'Review & schedule'}</p>
      <h1 className="sched__title">{heading?.title ?? 'A message across time.'}</h1>

      {previewUrl !== null ? (
        <video className="sched__preview" src={previewUrl} controls playsInline />
      ) : letterText !== undefined ? (
        <p className="sched__preview sched__letter-preview" style={{ whiteSpace: 'pre-wrap' }}>
          {letterText}
        </p>
      ) : null}

      <form onSubmit={onSubmit}>
        {load === 'loading' && <p role="status" className="sched__muted">Gathering the people you love…</p>}
        {load === 'error' && <p role="alert" className="sched__alert">{error}</p>}
        {load === 'empty' && <p className="sched__muted">Your vault has no recipients yet. Add someone first, then return to record for them.</p>}
        {load === 'ready' && (
          <>
            <label className="sched__field">
              <span className="sched__label">For</span>
              <select
                className="sched__input"
                value={recipientId}
                disabled={lockedRecipientId !== undefined}
                onChange={(e) => setRecipientId(e.target.value)}
              >
                {recipients.map((r) => (
                  <option key={r.id} value={r.id}>{recipientLabel(r)} · {r.relationship}</option>
                ))}
              </select>
            </label>

            <ConditionFields ref={primaryRef} minDate={minDate} idPrefix="c1" onError={setError} compoundMember={allowCompound && compound} />

            {allowCompound && (
              <label className="sched__mode" style={{ marginTop: 12 }}>
                <input type="checkbox" checked={compound} onChange={(e) => setCompound(e.target.checked)} />
                <span>Combine with another condition</span>
              </label>
            )}

            {allowCompound && compound && (
              <>
                <fieldset className="sched__modes">
                  <legend className="sched__label">Deliver when…</legend>
                  <label className="sched__mode">
                    <input type="radio" name="combinator" checked={combinator === 'AND'} onChange={() => setCombinator('AND')} />
                    <span>both have happened</span>
                  </label>
                  <label className="sched__mode">
                    <input type="radio" name="combinator" checked={combinator === 'OR'} onChange={() => setCombinator('OR')} />
                    <span>either one happens (whichever comes first)</span>
                  </label>
                </fieldset>
                <ConditionFields ref={secondRef} minDate={minDate} idPrefix="c2" onError={setError} compoundMember />
              </>
            )}

            {error && <p role="alert" className="sched__alert">{error}</p>}
          </>
        )}

        <div className="sched__actions">
          <button type="button" className="la-btn-secondary" onClick={onBack}>{backLabel}</button>
          <div className="la-cta-wrap">
            <button type="submit" className="la-btn-primary" disabled={load !== 'ready'}>Continue</button>
          </div>
        </div>
      </form>
    </section>
  );
}
